import { type MeDto, modulePermission, type Scope, visiblePermissions as visibleFor } from '@bqcrm/shared';
import { notifications } from '@mantine/notifications';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { api, HttpError } from './api';

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api<MeDto>('/auth/me');
      } catch (e) {
        if (e instanceof HttpError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
  });
}

/** Czy użytkownik ma uprawnienie (opcjonalnie w danym zakresie). Interfejs tylko ukrywa — decyduje serwer. */
export function can(me: MeDto | null | undefined, permission: string, scope?: Scope): boolean {
  const scopes = me?.permissions[permission];
  if (!scopes?.length) return false;
  return scope ? scopes.includes(scope) : true;
}

/** Dostęp do modułu lewego menu (uprawnienie `modul.<kod>`, docs/06 §5). */
export const canModule = (me: MeDto | null | undefined, code: string) => can(me, modulePermission(code));

/**
 * „Nowa sprawa” w panelu: zakładanie sprawy i wybór klienta wymagają też podglądu spraw (API: sprawy.podglad:W).
 * Monter z samym „zgłoś sprawę” (sprawy.tworzenie, zakres własny) zgłasza ją w aplikacji terenowej.
 */
export const canQuickCase = (me: MeDto | null | undefined) => can(me, 'sprawy.tworzenie') && can(me, 'sprawy.podglad', 'W');

/** Uprawnienia do pokazania w edytorach ról i praw — bez modułów opcjonalnych wyłączonych w systemie (Flota, Magazyn). */
export const visiblePermissions = (me: MeDto | null | undefined) => visibleFor(me?.features);

const ACCOUNT_KEY = 'bqcrm-konto';
const SWITCH_FLAG = 'bqcrm-konto-zmienione';
const store = {
  get: (s: Storage, k: string) => {
    try {
      return s.getItem(k);
    } catch {
      return null;
    }
  },
  set: (s: Storage, k: string, v: string | null) => {
    try {
      if (v === null) s.removeItem(k);
      else s.setItem(k, v);
    } catch {
      // przeglądarka bez dostępu do pamięci — bez ochrony między kartami
    }
  },
};

/**
 * Wszystkie karty przeglądarki dzielą jedno ciasteczko sesji. Gdy w innej karcie ktoś zaloguje się na inne konto
 * (albo wyloguje), ta karta pokazywałaby stare konto, a pytała serwer jako nowe. Wykrywamy to (zdarzenie `storage`
 * i sprawdzenie przy powrocie do karty) i odświeżamy kartę z komunikatem.
 */
export function useAccountSwitchGuard(me: MeDto | null | undefined, isLoading: boolean) {
  const current = useRef<string | null | undefined>(undefined);

  // stan z serwera jest prawdą — zapamiętujemy go dla pozostałych kart
  useEffect(() => {
    if (isLoading || me === undefined) return;
    // własne logowanie / wylogowanie tej karty — zdarzenie storage dostaną tylko pozostałe karty
    const id = me?.id ?? null;
    current.current = id;
    store.set(localStorage, ACCOUNT_KEY, id ?? '');
  }, [me, isLoading]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== ACCOUNT_KEY || current.current === undefined) return;
      const other = e.newValue || null;
      if (other !== current.current) switched();
    };
    // powrót do karty: sesja mogła się zmienić bez zdarzenia storage (np. karta była uśpiona)
    const onVisible = async () => {
      if (document.visibilityState !== 'visible' || current.current === undefined) return;
      let id: string | null;
      try {
        id = (await api<MeDto>('/auth/me')).id;
      } catch (e) {
        if (!(e instanceof HttpError && e.status === 401)) return;
        id = null;
      }
      if (id !== current.current) switched();
    };
    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // komunikat po odświeżeniu
  useEffect(() => {
    if (isLoading || me === undefined || !store.get(sessionStorage, SWITCH_FLAG)) return;
    store.set(sessionStorage, SWITCH_FLAG, null);
    notifications.show({
      color: 'orange',
      title: me ? 'Zmieniono konto w innej karcie' : 'Wylogowano w innej karcie',
      message: me ? `Ta karta działa teraz jako ${me.fullName}. Do pracy na dwóch kontach naraz użyj okna incognito albo innego profilu przeglądarki.` : 'W innej karcie wylogowano się — zaloguj się ponownie.',
      autoClose: 15_000,
    });
  }, [me, isLoading]);
}

function switched() {
  store.set(sessionStorage, SWITCH_FLAG, '1');
  window.location.reload();
}

/** Uprawnienia do pokazania w edytorach (bez modułów opcjonalnych wyłączonych w systemie). */
export function useVisiblePermissions() {
  return visiblePermissions(useMe().data);
}
